import { Button } from "../design/button";
import { Heading, Subheading } from "../design/heading";
import { useAuth } from "../contexts/AuthContext";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";

interface ErrorViewProps {
  error: Error;
}

// Helper function to convert URLs in text to clickable links
const renderMessageWithLinks = (message: string) => {
  // Check for Firebase console URLs and replace with "here" link
  const firebaseConsoleRegex = /(https:\/\/console\.firebase\.google\.com\/[^\s]+)/g;
  
  if (firebaseConsoleRegex.test(message)) {
    const url = message.match(firebaseConsoleRegex)?.[0];
    if (url) {
      // Replace the entire URL with "here" link
      const textWithoutUrl = message.replace(firebaseConsoleRegex, '');
      return (
        <>
          {textWithoutUrl}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            here
          </a>
        </>
      );
    }
  }
  
  // Fallback to original URL detection for other URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = message.split(urlRegex);
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline hover:text-blue-800"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const ErrorView: React.FC<ErrorViewProps> = ({ error }) => {
  const { user } = useAuth();

  const handleRetry = async () => {
    await user?.getIdToken(true);
    window.location.reload();
  };

  return (
    <div className="flex items-center justify-center">
      <div className="flex flex-col items-center justify-center">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-500" />
        <Heading className="mt-2">Error</Heading>
        <Subheading className="mt-2">
          {renderMessageWithLinks(error.message)}
        </Subheading>
        <Button className="mt-4" onClick={handleRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
};

export default ErrorView;
